import { Box, Grid, SxProps } from '@mui/material';
import { Logo, SpecialtyLogo, PumpkinLogo, BunnyLogo } from '@assets/svgs';
import { Link, useNavigate } from 'react-router-dom';
import { CSSProperties, useEffect, useState } from 'react';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { useStore } from '@store/GlobalStore';
import { useLayoutStore } from '@store/LayoutStore';
import { shallow } from 'zustand/shallow';
import { useGetBrokerage } from '@services/brokerage/useGetBrokerage';
import { BrokerageDivisionType } from '@lio/rule-engine';
import { getTodaysHoliday, HolidayMode } from '@theme/palettes/getPalette';
import { useApiEnvStore } from '@utils/api-client';

type Props = {
  containerStyle?: SxProps;
  iconStyle?: CSSProperties;
  hasUnsavedChanges?: boolean;
  smallLogo?: boolean;
};

export const HeaderLogo = ({ smallLogo, containerStyle = {}, iconStyle = {}, hasUnsavedChanges = false }: Props) => {
  const [showModal, setShowModal] = useState(false);

  const [currentUser, { isBroker, isCSR }] = useStore((state) => [state.currentUser, state.userAuth], shallow);

  const { closeHeaderMenu } = useLayoutStore(
    (state) => ({
      closeHeaderMenu: state.closeHeaderMenu,
    }),
    shallow,
  );
  const navigate = useNavigate();

  const currentBrokerageId = currentUser?.brokerageUsers?.[0]?.brokerageId;
  const { brokerage, isLoading: isBrokerageLoading } = useGetBrokerage({
    brokerageId: currentBrokerageId || '',
    isEnabled: !!currentBrokerageId && (isBroker || isCSR),
  });

  const [disableHolidayModeFlag] = useApiEnvStore((state) => [state.disableHolidayModeFlag], shallow);

  const holiday = getTodaysHoliday();

  const isBrokerageWholesale = brokerage?.brokerageDivision?.type === BrokerageDivisionType.Wholesale;

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    localStorage.setItem('logo', isBrokerageWholesale ? 'lsic-logo' : 'default-logo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrokerageWholesale]);

  // store in browser local storage to persist across page reloads
  const storedLogo = localStorage.getItem('logo') ?? '';

  const showLSICLogo = isBrokerageLoading ? storedLogo === 'lsic-logo' : isBrokerageWholesale;

  return (
    <>
      <Grid
        sx={{
          lineHeight: 0,
          ...containerStyle,
        }}
      >
        <Link
          to='/'
          onClick={(e) => {
            if (hasUnsavedChanges) {
              e.preventDefault();
              setShowModal(true);
            }
            closeHeaderMenu();
          }}
        >
          {holiday === HolidayMode.Halloween && !disableHolidayModeFlag ? (
            <Box
              component='img'
              src={PumpkinLogo}
              alt='Pumpkin Logo'
              style={{
                width: smallLogo ? 90 : 110,
                height: 'auto',
                marginTop: smallLogo ? 0 : 15,
                position: 'relative',
              }}
            />
          ) : holiday === HolidayMode.Easter && !disableHolidayModeFlag ? (
            <Box
              component='img'
              src={BunnyLogo}
              alt='Easter Bunny Logo'
              style={{
                width: smallLogo ? 90 : 110,
                height: 'auto',
                marginTop: smallLogo ? 0 : 10,
                position: 'relative',
                filter: 'brightness(0) invert(1)',
              }}
            />
          ) : showLSICLogo ? (
            <SpecialtyLogo aria-label='lsic-logo' style={iconStyle} />
          ) : (
            <Logo aria-label='logo' style={iconStyle} />
          )}
        </Link>
      </Grid>
      {showModal && (
        <ConfirmationDialog
          isOpen={showModal}
          onCancel={() => setShowModal(false)}
          description='There are unsaved changes. Exit without saving?'
          buttonConfig={{
            isConfirmButtonDisabled: false,
            isSecondaryButtonDisabled: false,
            onSecondaryButtonClick: () => {
              setShowModal(false);
            },
            confirmText: 'Yes, exit without saving',
            secondaryText: 'No, return to quote',
            onConfirm: () => {
              setShowModal(false);
              navigate('/');
            },
          }}
        />
      )}
    </>
  );
};
